import { useEffect, useMemo, useRef, useState } from "react"
import { Box, Button, createListCollection, HStack, Select, Text, VStack } from "@chakra-ui/react"
import { type ColumnDef } from "@tanstack/react-table"
import DataTable from "../../framework/DataTable"
import ErrorAlert from "../../components/ErrorAlert"
import { apiFetch } from "../../framework/api"
import { useAuthToken } from "../../hooks/useAuthToken"
import { extractErrorInfo } from "../../lib/api/error-utils"

type Day = { date: string; openLiftCount: number; successfulCount: number; partialCount: number; failedCount: number }
type Resort = { date: string; resortUid: string; resortName: string; source: string; openLiftCount: number; liftCount: number; outcome: string }
type History = { from: string; to: string; days: Day[]; resorts: Resort[] }

const rangeCollection = createListCollection({
  items: [
    { label: "Last 30 days", value: "30" },
    { label: "Last 90 days", value: "90" },
    { label: "Last year", value: "365" },
    { label: "All history", value: "all" },
  ],
})

function dateBefore(days: number) {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return date.toISOString().slice(0, 10)
}

function formatChartDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
    new Date(`${value}T00:00:00Z`),
  )
}

function DailyChart({ days }: { days: Day[] }) {
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(800)
  const height = 260
  const margin = { top: 24, right: 24, bottom: 58, left: 72 }
  const plotWidth = width - margin.left - margin.right
  const plotHeight = height - margin.top - margin.bottom

  useEffect(() => {
    const container = chartContainerRef.current
    if (!container) return

    const observer = new ResizeObserver(([entry]) => {
      setWidth(Math.max(640, Math.floor(entry.contentRect.width)))
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const segments = useMemo(() => {
    const usableDays = days.filter((day) => day.successfulCount + day.partialCount > 0)
    const max = Math.max(1, ...usableDays.map((day) => day.openLiftCount))
    const tickStep = Math.max(1, Math.ceil(max / 4))
    const yMax = tickStep * 4
    const output: string[] = []
    let segment: string[] = []
    days.forEach((day, index) => {
      if (day.successfulCount + day.partialCount === 0) {
        if (segment.length > 0) output.push(segment.join(" "))
        segment = []
        return
      }
      const x = margin.left + (index / Math.max(1, days.length - 1)) * plotWidth
      const y = margin.top + plotHeight - (day.openLiftCount / yMax) * plotHeight
      segment.push(`${x},${y}`)
    })
    if (segment.length > 0) output.push(segment.join(" "))
    return { segments: output, yMax }
  }, [days, plotHeight, plotWidth])

  const yTicks = Array.from({ length: 5 }, (_, index) => (segments.yMax / 4) * index)
  const xTickIndexes = Array.from(
    new Set(
      Array.from({ length: Math.min(4, days.length) }, (_, index) =>
        Math.round((index * Math.max(0, days.length - 1)) / Math.max(1, Math.min(4, days.length) - 1)),
      ),
    ),
  )

  if (days.length === 0) return <Text color="gray.400">No daily snapshots in this range.</Text>

  return (
    <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p="3">
      <HStack justify="space-between" mb="2">
        <Text fontWeight="semibold">Total open lifts</Text>
        <Text color="gray.300">{days.at(-1)?.openLiftCount ?? 0} latest</Text>
      </HStack>
      <Box ref={chartContainerRef} overflowX="auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          style={{ display: "block" }}
          role="img"
          aria-label="Daily total open lifts over time"
        >
          <text x={18} y={height / 2} fill="#a0aec0" fontSize="12" textAnchor="middle" transform={`rotate(-90 18 ${height / 2})`}>
            Open lifts
          </text>
          {yTicks.map((tick) => {
            const y = margin.top + plotHeight - (tick / segments.yMax) * plotHeight
            return (
              <g key={tick}>
                <line x1={margin.left} x2={width - margin.right} y1={y} y2={y} stroke="#3f3f46" strokeWidth="1" />
                <text x={margin.left - 10} y={y + 4} fill="#a0aec0" fontSize="12" textAnchor="end">
                  {tick.toLocaleString()}
                </text>
              </g>
            )
          })}
          <line x1={margin.left} x2={margin.left} y1={margin.top} y2={height - margin.bottom} stroke="#718096" strokeWidth="1" />
          <line x1={margin.left} x2={width - margin.right} y1={height - margin.bottom} y2={height - margin.bottom} stroke="#718096" strokeWidth="1" />
          {xTickIndexes.map((index) => {
            const day = days[index]
            const x = margin.left + (index / Math.max(1, days.length - 1)) * plotWidth
            return (
              <g key={day.date}>
                <line x1={x} x2={x} y1={height - margin.bottom} y2={height - margin.bottom + 5} stroke="#718096" strokeWidth="1" />
                <text x={x} y={height - margin.bottom + 22} fill="#a0aec0" fontSize="12" textAnchor="middle">
                  {formatChartDate(day.date)}
                </text>
              </g>
            )
          })}
          <text x={margin.left + plotWidth / 2} y={height - 12} fill="#a0aec0" fontSize="12" textAnchor="middle">
            Date
          </text>
          {segments.segments.map((points, index) => (
            <polyline key={index} points={points} fill="none" stroke="#63b3ed" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {days.map((day, index) => {
            if (day.successfulCount + day.partialCount === 0) return null
            const x = margin.left + (index / Math.max(1, days.length - 1)) * plotWidth
            const y = margin.top + plotHeight - (day.openLiftCount / segments.yMax) * plotHeight
            const incomplete = day.partialCount > 0 || day.failedCount > 0
            return <circle key={day.date} cx={x} cy={y} r={incomplete ? 5 : 3.5} fill={incomplete ? "#f6ad55" : "#63b3ed"} stroke="#171923" strokeWidth="2" />
          })}
        </svg>
      </Box>
      <HStack gap="4" mt="2" fontSize="sm" color="gray.400" flexWrap="wrap">
        <HStack gap="2"><Box boxSize="3" borderRadius="full" bg="blue.300" /><Text>Complete data</Text></HStack>
        <HStack gap="2"><Box boxSize="3" borderRadius="full" bg="orange.300" /><Text>Partial data or failed resort samples</Text></HStack>
        <Text>Gaps indicate days with no usable resort readings.</Text>
      </HStack>
    </Box>
  )
}

const columns: ColumnDef<Resort>[] = [
  { accessorKey: "date", header: "Date" },
  { accessorKey: "resortName", header: "Resort" },
  { accessorKey: "source", header: "Source" },
  { accessorKey: "openLiftCount", header: "Open lifts" },
  { accessorKey: "liftCount", header: "Mapped lifts" },
  { accessorKey: "outcome", header: "Outcome" },
]

function LatestSnapshotTotals({ history }: { history: History | null }) {
  const latestDate = history?.days.at(-1)?.date
  const latestRows = history?.resorts.filter((row) => row.date === latestDate) ?? []
  const usableRows = latestRows.filter((row) => row.outcome !== "failed")
  const openLiftCount = usableRows.reduce((total, row) => total + row.openLiftCount, 0)
  const mappedLiftCount = usableRows.reduce((total, row) => total + row.liftCount, 0)
  const resortCount = new Set(latestRows.map((row) => row.resortUid)).size

  return (
    <VStack align="stretch" gap="4">
      <HStack align="stretch" gap="3" flexWrap="wrap">
        <Metric label="Open lifts" value={openLiftCount} />
        <Metric label="Mapped lifts" value={mappedLiftCount} />
        <Metric label="Resorts" value={resortCount} />
      </HStack>
      {latestDate && (
        <Text color="gray.500" fontSize="xs">
          Latest snapshot: {latestDate}
        </Text>
      )}
    </VStack>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" minW="140px" px="4" py="3">
      <Text color="gray.400" fontSize="sm">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="semibold">
        {value.toLocaleString()}
      </Text>
    </Box>
  )
}

export default function OpeningsHistoryDashboard() {
  const token = useAuthToken()
  const [range, setRange] = useState("90")
  const [history, setHistory] = useState<History | null>(null)
  const [loading, setLoading] = useState(false)
  const [runningSnapshot, setRunningSnapshot] = useState(false)
  const [snapshotMessage, setSnapshotMessage] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const from = range === "all" ? "" : dateBefore(Number(range) - 1)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (from) params.set("from", from)
    void apiFetch<History>(`/admin/openings-history?${params.toString()}`, { token, method: "GET" })
      .then((response) => { if (active) setHistory(response) })
      .catch((err) => { if (active) setError(err) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [from, token])

  const errorInfo = error ? extractErrorInfo(error) : null
  const runSnapshot = async () => {
    setRunningSnapshot(true)
    setSnapshotMessage(null)
    try {
      await apiFetch<{ started: boolean }>("/admin/openings-history/snapshot", { token, method: "POST" })
      setSnapshotMessage("Snapshot started. Reload shortly to see completed readings.")
    } catch (err) {
      setError(err)
    } finally {
      setRunningSnapshot(false)
    }
  }

  return (
    <VStack align="stretch" gap="4">
      <HStack justify="space-between" flexWrap="wrap">
        <Text fontSize="lg" fontWeight="semibold">
          Lift openings
        </Text>
        <HStack>
          <Button
            size="sm"
            colorPalette="blue"
            onClick={() => void runSnapshot()}
            loading={runningSnapshot}
          >
            Run snapshot
          </Button>
          <Select.Root
            collection={rangeCollection}
            value={[range]}
            onValueChange={(event) => setRange(String(event.value[0] ?? "90"))}
            width="160px"
          >
            <Select.HiddenSelect />
            <Select.Control>
              <Select.Trigger>
                <Select.ValueText />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Select.Positioner>
              <Select.Content>
                {rangeCollection.items.map((item) => (
                  <Select.Item item={item} key={item.value}>
                    {item.label}
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Select.Root>
        </HStack>
      </HStack>

      {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}
      {snapshotMessage && <Text color="green.300">{snapshotMessage}</Text>}

      <LatestSnapshotTotals history={history} />
      <DailyChart days={history?.days ?? []} />

      <Box overflowX="auto">
        <DataTable
          columns={columns}
          data={history?.resorts ?? []}
          isLoading={loading}
          emptyMessage="No resort opening snapshots in this range."
        />
      </Box>
    </VStack>
  )
}
